import { useState, useEffect, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Header from "./components/Header";
import { Box, createTheme, Typography } from "@mui/material";

import { App as CapacitorApp } from "@capacitor/app";

import "@fontsource/beau-rivage";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
// import APP_CONFIG from "./config/constants";

import { Capacitor } from "@capacitor/core";
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar";
import theme from "./theme";
import LoginPage from "./components/LoginPage";
import OTPVerificationPage from "./components/OTPVerificationPage";
import {
  AutoTaskAuthContext,
  AutoTaskContext,
} from "./Context/AutoTaskContext";
import { TimerProvider } from "./Context/TimerContext";
import { LedgerStateProvider } from "./Context/LedgerStateContext";
import { PawnModeProvider } from "./Context/PawnModeContext";
import HomePage from "./components/HomePage";
import AllServiceTicketsPage from "./components/AllServiceTicketsPage";
import RaiseServiceTicket from "./components/RaiseServiceTicket";
import Report from "./components/Report";
import BranchReport from "./components/BranchReport";

import TicketPanel from "./components/TicketPanel";
import MyTickets from "./components/MyTickets";
import StoreMaster from "./components/masters/StoreMaster";
import UserMaster from "./components/masters/UserMaster";
import DepartmentMaster from "./components/masters/DepartmentMaster";
import DayEndReport from "./components/DayEndReport";
import PerformanceStats from "./components/PerformanceStats";
import StaffDirectory from "./components/StaffDirectory";
import StoreDirectory from "./components/StoreDirectory";
import { useMediaQuery, useTheme } from "@mui/material";

import { useSafeAreaTop, useSafeAreaBottom } from "./SafeArea";
import SearchAnyTicket from "./components/SearchAnyTicket";
import CeoDashboard from "./components/CeoDashboard";
import CustomerMaster from "./components/TravelERP/CustomerMaster";
import DriverMaster from "./components/TravelERP/DriverMaster";
import VehicleMaster from "./components/TravelERP/VehicleMaster";
import LandingPage from "./components/TravelERP/LandingPage";
import TravelSplash from "./components/TravelERP/TravelSplash";
import TravelBookingEntry from "./components/TravelERP/TravelBookingEntry";
import TravelDashboard from "./components/TravelERP/DayWiseDashboard";
import AccountMasterLayout from "./components/Accounts/AccountMasterLayout";
import AdvancedSettings from "./components/Accounts/AdvancedSettings";
import DashboardHandler from "./components/TravelERP/DashboardHandler";
import AccountEntry from "./components/Accounts/AccountEntry";
import CostCenterMaster from "./components/Accounts/CostCenterMaster";
import Ledger from "./components/Accounts/Ledger";
import TrialBalanceByGroup from "./components/Accounts/TrialBalanceByGroup";
import ProjectManagement from "./components/ProjectManagement";
import ItineraryMaster from "./components/TravelERP/ItineraryMaster";
import InvoiceGenerator from "./components/Accounts/InvoiceGenerator";
import ReminderMaster from "./components/ReminderMaster";
import CompanySettings from "./components/CompanySettings";
import CashBankBook from "./components/Accounts/CashBankBook";
import WorkTracker from "./components/WorkTracker";
// import HsnMaster from "./components/JewelMaster/HsnMaster";

// JEWEL MASTER
import JewelMasterSplash from "./components/JewelMaster/JewelMasterSplash";
import JewelMasterDashboard from "./components/JewelMaster/JewelMasterDashboard";
import PrefixConfig from "./components/JewelMaster/PrefixConfig";
import ItemTypeMaster from "./components/JewelMaster/ItemTypeMaster";
// The Metal Type / Purity / Floor masters (moved into the Pawn module, styled
// to the pawn design, still on the /api/jewel-master API) are now all rendered
// through the unified masters screen below — legacy routes redirect into it.
import PawnMasters from "./components/PawnShop/PawnMasters";

// PAWN SHOP
import PawnShopDashboard from "./components/PawnShop/PawnShopDashboard";
import PawnTicketGenerator from "./components/PawnShop/PawnTicketGenerator";
import PawnCustomerMaster from "./components/PawnShop/PawnCustomerMaster";
import PaymentScreen from "./components/PawnShop/PaymentScreen";
import PawnReports from "./components/PawnShop/PawnReports";
import PawnBarcodeMapper from "./components/PawnShop/PawnBarcodeMapper";
import StockCheckScreen from "./components/PawnShop/StockCheckScreen";
import PawnSettings from "./components/PawnShop/PawnSettings";
// The in-app walkthrough of the whole module, reached from the sidebar.
import PawnTutorial from "./components/PawnShop/PawnTutorial";
// Excel migration home — bring an old pawn software's records in from a sheet.
import PawnImportData from "./components/PawnShop/PawnImportData";

import ATSModule from "./components/atsModule";
import AiInterview from "./components/hiring/AiInterview";
import PawnLayout from "./components/PawnShop/PawnLayout";
import PawnReceiptScreen from "./components/PawnShop/PawnReceiptScreen";
import PawnSettlementScreen from "./components/PawnShop/PawnSettlementScreen";
import PawnTicketView from "./components/PawnShop/PawnTicketView";
import { getStoreHome } from "./utils/moduleNav";

import DepartmentsHub from "./components/masters/DepartmentsHub";
import DepartmentOrgChart from "./components/masters/DepartmentOrgChart";

// Helper component to check if current route is a pawn shop route
const isPawnRoute = (pathname) => {
  return pathname.startsWith("/pawn");
};

// The one pawn screen that belongs to nobody: it is opened by scanning the QR
// code on a printed ticket, so the person reading it is a customer with no
// login and never will have one. Every OTHER /pawn route is a counter screen
// that needs a session — see the guard in the pawn branch below.
const isPublicPawnRoute = (pathname) => {
  return pathname.startsWith("/pawn/ticket-view");
};

const AppLayout = () => {
  const topInset = useSafeAreaTop();
  const bottomInset = useSafeAreaBottom();
  const isIOS = Capacitor.getPlatform() === "ios";

  const safeAreaTop = topInset;
  const safeAreaBottom = bottomInset;
  const { userInfo, currentStoreInfo, loadingUserData } =
    useContext(AutoTaskAuthContext);
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md")); // uses theme value

  // Create a responsive theme by overriding background colors
  const responsiveTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      background: {
        paper: isMobile ? "#ffff" : "#f4f6f8",
        default: isMobile ? "#f4f6f8" : "#ffff",
      },
    },
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [staffName, setStaffName] = useState(null);

  const isLoginRoute =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/verify-otp";

  // Check if current route is a pawn shop route
  const isPawnModuleRoute = isPawnRoute(location.pathname);
  const isPublicPawnModuleRoute = isPublicPawnRoute(location.pathname);

  // Check if current route is ATS module
  const isATSModule = location.pathname.startsWith("/ats");

  // Candidate-facing interview. PUBLIC — the person opening it is not a user of
  // this app and never will be; they have a signed link from an email.
  //
  // Checked before the auth guard below and rendered before any layout, because
  // both would otherwise redirect them to the login screen. The link's token is
  // the only credential, and it is verified server-side on every call.
  const isPublicInterviewRoute = location.pathname.startsWith("/interview");

  // Force render login page if user data is unavailable.
  //
  // The pawn tree is skipped here but NOT unguarded: it is a self-contained
  // host that returns before this component ever reaches the shared layout, so
  // it turns a signed-out session away during its own render (see the pawn
  // branch below) instead of one render later. Anything added here must be
  // added there too.
  useEffect(() => {
    if (
      !loadingUserData &&
      !userInfo &&
      !isLoginRoute &&
      !isPawnModuleRoute &&
      !isPublicInterviewRoute
    ) {
      navigate("/", { replace: true }); // redirect only if not on a login route, pawn module, or public interview
    }
  }, [
    userInfo,
    loadingUserData,
    location.pathname,
    navigate,
    isLoginRoute,
    isPawnModuleRoute,
    isPublicInterviewRoute,
  ]);

  useEffect(() => {
    let backHandlerArrowButton;
    CapacitorApp.addListener("backButton", () => {
      if (location.pathname === "/" || location.pathname === "/dashboard") {
        CapacitorApp.exitApp(); // Exit the app
      } else {
        navigate(-1); // Go to the page on the previous route
      }
    }).then((handler) => {
      backHandlerArrowButton = handler;
    });

    return () => {
      setSidebarOpen(false);
      if (backHandlerArrowButton) backHandlerArrowButton.remove();
    }; // eslint-disable-next-line
  }, [location.pathname]);

  const handleStaffNameOnHeader = (name) => {
    setStaffName(name);
  };

  // Candidate interview — rendered before every layout and before the ATS
  // branch. It carries its own full-page chrome because the app's header,
  // sidebar and footer all read the logged-in user, which a candidate does not
  // have. Placed first so no other branch can claim the route.
  if (isPublicInterviewRoute) {
    return (
      <ThemeProvider theme={responsiveTheme}>
        <CssBaseline />
        <Routes>
          <Route path="/interview" element={<AiInterview />} />
        </Routes>
      </ThemeProvider>
    );
  }

  // For ATS module - render without any layout
  if (isATSModule) {
    return <ATSModule />;
  }

  // For Pawn module - render without header, sidebar, and footer
  if (isPawnModuleRoute) {
    // A pawn counter screen needs a session, exactly like every other module's
    // screens do. Enforced HERE, during render, rather than in the effect
    // above: an effect only fires AFTER a render, so a signed-out session
    // landing on /pawn/home would mount the dashboard, fire its fetches with
    // no store and paint "No store selected." before any redirect could run.
    // Walking BACK into the pawn tree after logging out of another module is
    // exactly that path — the pages behind the login screen belong to a
    // session that no longer exists.
    //
    // While the stored session is still being read back, nothing is known yet
    // either way, so hold the blank screen instead of guessing: `userInfo` is
    // null during hydration too, and redirecting on it would throw a signed-in
    // shop out on every reload.
    if (!isPublicPawnModuleRoute) {
      if (loadingUserData) {
        return (
          <ThemeProvider theme={responsiveTheme}>
            <CssBaseline />
            <Box sx={{ height: "100vh" }} />
          </ThemeProvider>
        );
      }
      if (!userInfo) {
        return <Navigate to="/" replace />;
      }
    }

    return (
      <ThemeProvider theme={responsiveTheme}>
        <CssBaseline />
        <Routes>
          {/* Public, standalone read-only ticket viewer opened by scanning the
              QR code on a printed pawn ticket. Rendered without PawnLayout
              chrome so it reads like a shareable e-invitation card. */}
          <Route path="/pawn/ticket-view" element={<PawnTicketView />} />
          <Route element={<PawnLayout />}>
            <Route path="/pawn/home" element={<PawnShopDashboard />} />
            <Route
              path="/pawn/ticket-generator"
              element={<PawnTicketGenerator />}
            />
            <Route
              path="/pawn/customer-master"
              element={<PawnCustomerMaster />}
            />
            <Route path="/pawn/payment" element={<PaymentScreen />} />
            {/* Unified reports screen (Payment / Receipt / Settlement chips). */}
            <Route path="/pawn/reports" element={<PawnReports />} />
            {/* Legacy per-report routes redirect into the unified screen with
                the matching tab pre-selected, so old links keep working. */}
            <Route
              path="/pawn/payment-report"
              element={<Navigate to="/pawn/reports?type=payment" replace />}
            />
            <Route
              path="/pawn/receipt-report"
              element={<Navigate to="/pawn/reports?type=receipt" replace />}
            />
            <Route
              path="/pawn/settlement-report"
              element={<Navigate to="/pawn/reports?type=settlement" replace />}
            />
            <Route path="/pawn/settings" element={<PawnSettings />} />
            <Route path="/pawn/receipt" element={<PawnReceiptScreen />} />
            {/* Same sheet as /pawn/receipt, mounted with settlement mode on —
                the dedicated close-out counter. */}
            <Route path="/pawn/settlement" element={<PawnSettlementScreen />} />
            <Route path="/pawn/stock-check" element={<StockCheckScreen />} />

            {/* ---------------------------------------------------------------
                Cloud Accounting, mounted INSIDE the pawn route tree.
                Same components the Daily Tick / Travel hosts use — mirroring
                them here keeps the pawn sidebar on screen and lets their
                home button land on the pawn dashboard instead of the travel
                one. The screens detect the /pawn prefix and render themselves
                in the pawn skin (see Accounts/pawnAccountsTheme.js).
            --------------------------------------------------------------- */}
            <Route path="/pawn/accounts" element={<AccountMasterLayout />} />
            <Route path="/pawn/accounts-ledger" element={<Ledger />} />
            <Route path="/pawn/accounts-entry" element={<AccountEntry />} />
            <Route
              path="/pawn/accounts-cost-center"
              element={<CostCenterMaster />}
            />

            {/* Unified masters screen (Metal Type / Floor chips). */}
            <Route path="/pawn/masters" element={<PawnMasters />} />
            {/* Legacy per-master routes redirect into the unified screen with
                the matching tab pre-selected, so old links keep working. */}
            <Route
              path="/pawn/metal-type-master"
              element={<Navigate to="/pawn/masters?type=metal" replace />}
            />
            <Route
              path="/pawn/purity-master"
              element={<Navigate to="/pawn/masters?type=purity" replace />}
            />
            <Route
              path="/pawn/floor-master"
              element={<Navigate to="/pawn/masters?type=floor" replace />}
            />

            {/* Barcode label mapper — upload the printer's PRN template and map
                its <KEYWORD> placeholders to loan fields. Set up once per store
                by the installation engineer. */}
            <Route
              path="/pawn/barcode-mapper"
              element={<PawnBarcodeMapper />}
            />

            {/* Guided walkthrough of the module, screen by screen. Inside the
                layout so the sidebar stays put — a chapter's "Open this screen"
                button navigates away and the reader comes straight back. */}
            <Route path="/pawn/tutorial" element={<PawnTutorial />} />

            {/* Data migration: pick a dataset, download its template, upload the
                filled sheet. Inside the layout so the sidebar stays put — a
                shop works through the four datasets in order. */}
            <Route path="/pawn/import-data" element={<PawnImportData />} />
          </Route>
        </Routes>
      </ThemeProvider>
    );
  }

  // Render nothing on the screen while loading user data
  if (loadingUserData) {
    return (
      <ThemeProvider theme={responsiveTheme}>
        <CssBaseline />
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        ></Box>
      </ThemeProvider>
    );
  }

  const isDashboardRoute = location.pathname === "/home";

  if (isLoginRoute) {
    // A session already exists, so the login screens have nothing left to do —
    // send it to the home its store belongs to. This has to be module-aware
    // rather than a flat "/home": the OTP screen commits the user to context and
    // navigates in the same tick, and THIS redirect wins the race, so hard-coding
    // "/home" would bounce every pawn or travel login through the Daily Tick
    // dashboard before the OTP screen's own navigate could correct it.
    if (userInfo) {
      return <Navigate to={getStoreHome(currentStoreInfo)} replace />;
    }
    return (
      <ThemeProvider theme={responsiveTheme}>
        <CssBaseline />

        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-otp" element={<OTPVerificationPage />} />

          {/* <Route path="*" element={<NotFound />} /> */}
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={responsiveTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          minHeight: ["100vh", "-webkit-fill-available"],
          //---------------------------------------------------
          paddingTop: isIOS ? "var(--safe-area-top)" : safeAreaTop,
          paddingBottom: isIOS ? "var(--safe-area-bottom)" : safeAreaBottom,
          //---------------------------------------------------
          paddingLeft: "var(--safe-area-left)",
          paddingRight: "var(--safe-area-right)",
          padding: !isMobile ? 1 : 0,
          boxSizing: "border-box",
          overflowX: "hidden",
          gap: !isMobile ? 1 : 0,
        }}
      >
        {" "}
        {/* WEB RESPONSIVE */}
        {!isMobile && (
          <Box
            sx={{
              width: "56px",
            }}
          >
            <Sidebar />
          </Box>
        )}
        {/* WEB SIDE BAR */}
        <Box
          sx={{
            ...(!isMobile && {
              flex: 1,
              display: "flex",
              flexDirection: "column",

              transition: "margin-left 0.3s ease", // smooth shift on sidebar hover
            }),
          }}
        >
          {" "}
          <Box
            component="header"
            sx={{
              bgcolor: !isMobile ? "#fff" : "transparent",
              position: isMobile ? "fixed" : "fixed",
              top: 0,
              left: isMobile ? 0 : "64px",
              right: 0,
              width: !isMobile ? "calc(100vw - px)" : "100%",
              zIndex: 2000,
              paddingTop: isMobile
                ? isIOS
                  ? "var(--safe-area-top)"
                  : safeAreaTop
                : 1,
              paddingX: !isMobile ? "8px" : 0,
            }}
          >
            {/* Header */}
            <Header
              staffName={staffName}
              isDashboard={isDashboardRoute}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </Box>
          {/* Mobile Sidebar */}
          {isMobile && <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />}
          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flex: 1,
              mt: "56px",
              mb: isMobile ? "76px" : 0,
              px: isMobile ? 2 : 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",

              /* hide scrollbar but keep scrolling */
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            <Routes>
              <Route path="/home" element={<HomePage />} />
              <Route
                path="/all-service-tickets"
                element={<AllServiceTicketsPage />}
              />
              <Route
                path="/raise-service-ticket"
                element={<RaiseServiceTicket />}
              />
              <Route path="/ticket-panel" element={<TicketPanel />} />{" "}
              <Route
                path="/mytickets"
                element={
                  <MyTickets
                    handleStaffNameOnHeader={handleStaffNameOnHeader}
                  />
                }
              />
              <Route path="/create-store" element={<StoreMaster />} />
              <Route path="/create-user" element={<UserMaster />} />
              {/* Two kinds of department behind one sidebar entry: the ticket
                  departments this screen has always managed, and the org chart
                  that used to be reachable only from inside /ats. The org routes
                  are declared here rather than under /ats so drilling into a
                  department does not throw the user out of this shell. */}
              <Route path="/create-department" element={<DepartmentsHub />} />
              <Route
                path="/create-department/org"
                element={<DepartmentOrgChart />}
              />
              <Route
                path="/create-department/org/:deptId"
                element={<DepartmentOrgChart />}
              />
              <Route path="/create-department" element={<DepartmentMaster />} />
              <Route path="/performance" element={<PerformanceStats />} />
              <Route path="/report" element={<Report />} />
              {/* Branch-wise figures. The sidebar hides the entry on a
                  single-branch store, but the route stays reachable so a
                  bookmark from a store that HAS branches still resolves —
                  the server collapses it to that one branch.
                  A pawn store reaches the same screen from its own Reports
                  strip (/pawn/reports?type=branch) and is not offered this
                  entry on the rail — a stale link here forwards itself there
                  rather than stranding a pawn owner in the Daily Tick shell.
                  Either way the component refuses anyone but the owner, so
                  the open route is not a way in. */}
              <Route path="/branch-report" element={<BranchReport />} />
              <Route path="/team" element={<StaffDirectory />} />
              <Route path="/all-stores" element={<StoreDirectory />} />
              <Route path="/search-ticket" element={<SearchAnyTicket />} />
              <Route path="/ceo-dashboard" element={<CeoDashboard />} />
              <Route path="/timesheet" element={<WorkTracker />} />
              <Route path="/live-activity" element={<WorkTracker />} />
              {/* =================================================================== */}
              {/* Travel ERP Pages */}
              {/* =================================================================== */}
              <Route path="/travel/splash" element={<TravelSplash />} />
              <Route path="/travel/home" element={<LandingPage />} />
              <Route
                path="/travel/customer-master"
                element={<CustomerMaster />}
              />
              <Route path="/travel/driver-master" element={<DriverMaster />} />
              <Route
                path="/travel/vehicle-master"
                element={<VehicleMaster />}
              />
              <Route path="/travel/booking" element={<TravelBookingEntry />} />
              <Route path="/travel/dashboard" element={<DashboardHandler />} />
              <Route path="/travel/itinerary" element={<ItineraryMaster />} />
              {/* =================================================================== */}
              {/* Accounts */}
              {/* =================================================================== */}
              <Route path="/accounts" element={<AccountMasterLayout />} />
              <Route
                path="/accounts-advanced-settings"
                element={<AdvancedSettings />}
              />
              <Route
                path="/accounts-cost-center"
                element={<CostCenterMaster />}
              />
              <Route path="/accounts-entry" element={<AccountEntry />} />
              <Route path="/accounts-ledger" element={<Ledger />} />
              <Route path="/trial-balance" element={<TrialBalanceByGroup />} />
              <Route path="/cash-bank-book" element={<CashBankBook />} />
              <Route path="/invoice" element={<InvoiceGenerator />} />
              <Route
                path="/project-management"
                element={<ProjectManagement />}
              />
              <Route path="/reminders" element={<ReminderMaster />} />
              <Route path="/store-settings" element={<CompanySettings />} />
              {/* =================================================================== */}
              {/* JEWEL MASTER */}
              {/* =================================================================== */}
              <Route
                path="/jewel-master/splash"
                element={<JewelMasterSplash />}
              />
              <Route
                path="/jewel-master/home"
                element={<JewelMasterDashboard />}
              />
              {/* <Route
                path="/jewel-master/metal-type-master"
                element={<MetalTypeMaster />}
              /> */}
              <Route
                path="/jewel-master/item-type-master"
                element={<ItemTypeMaster />}
              />
              {/* <Route
                path="/jewel-master/purity-master"
                element={<PurityMaster />}
              /> */}
              <Route
                path="/jewel-master/prefix-config"
                element={<PrefixConfig />}
              />
              {/* <Route path="/hsn-master" element={<HsnMaster />} /> */}
              {/* <Route path="/stone-master" element={<StoneMaster />} /> */}
              {/* <Route path="/floor-master" element={<FloorMaster />} /> */}
            </Routes>
          </Box>
          {/* Footer */}
          {isMobile && (
            <Box
              component="footer"
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                paddingBottom: isIOS
                  ? "var(--safe-area-bottom)"
                  : safeAreaBottom,
              }}
            >
              <Footer handleStaffNameOnHeader={handleStaffNameOnHeader} />
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setBackgroundColor({ color: "#000000" });
      StatusBar.setStyle({ style: StatusBarStyle.Light });
    }

    const setSafeAreaVariables = () => {
      const style = document.documentElement.style;
      const vh = window.innerHeight;
      const vvh = window.visualViewport?.height || vh;
      const navHeight = vh - vvh;

      style.setProperty("--safe-area-top", "env(safe-area-inset-top, 24px)");
      style.setProperty(
        "--safe-area-bottom",
        `env(safe-area-inset-bottom, ${navHeight > 0 ? navHeight : 48}px)`,
      );
      style.setProperty("--safe-area-left", "env(safe-area-inset-left, 0px)");
      style.setProperty("--safe-area-right", "env(safe-area-inset-right, 0px)");
    };

    // Call immediately
    setSafeAreaVariables();

    // Delayed call to ensure stable layout
    const timeout = setTimeout(() => {
      setSafeAreaVariables();
    }, 100);

    // Add listeners
    window.addEventListener("resize", setSafeAreaVariables);
    window.visualViewport?.addEventListener("resize", setSafeAreaVariables);
    window.visualViewport?.addEventListener("scroll", setSafeAreaVariables);

    // Clean up everything
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", setSafeAreaVariables);
      window.visualViewport?.removeEventListener(
        "resize",
        setSafeAreaVariables,
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        setSafeAreaVariables,
      );
    };
  }, []);

  return (
    <AutoTaskContext>
      <TimerProvider>
        <PawnModeProvider>
          <LedgerStateProvider>
            {" "}
            <Router>
              <AppLayout />
            </Router>
          </LedgerStateProvider>
        </PawnModeProvider>
      </TimerProvider>
    </AutoTaskContext>
  );
}

export default App;
